/**
 * Pure version bookkeeping for Excalidraw's collaboration model (no Excalidraw
 * import, so it is unit-testable in node).
 *
 * Rule (Excalidraw's `reconcileElements` / the miro-sync server): for the same
 * element id the copy with the higher `version` wins; on equal `version` the
 * LOWER `versionNonce` wins. Deleted elements are tombstones (`isDeleted:
 * true`) and take part in the rule like any other element.
 *
 * `VersionLedger` remembers, per element id, the version/nonce the server is
 * known to have (we sent it, or we received it). Only elements that moved past
 * that are sent — the "changed elements only" half of the protocol — and an
 * element we just applied from a remote update is never echoed back.
 */

export interface VersionStamp {
  version: number;
  versionNonce: number;
}

export interface VersionedElement extends VersionStamp {
  id: string;
  isDeleted?: boolean;
}

/** True when `incoming` should replace `current` (Excalidraw's rule). */
export function incomingWins(current: VersionStamp | undefined | null, incoming: VersionStamp): boolean {
  if (!current) return true;
  if (incoming.version !== current.version) return incoming.version > current.version;
  return incoming.versionNonce < current.versionNonce;
}

export function sameStamp(a: VersionStamp | undefined | null, b: VersionStamp | undefined | null): boolean {
  return !!a && !!b && a.version === b.version && a.versionNonce === b.versionNonce;
}

/**
 * Server-side reconcile (what miro-sync does), used by the in-memory fake and
 * the tests: merges `incoming` into `store`, returns the accepted elements.
 */
export function serverMerge<T extends VersionedElement>(store: Map<string, T>, incoming: readonly T[]): T[] {
  const accepted: T[] = [];
  for (const el of incoming) {
    if (!el || typeof el.id !== 'string') continue;
    if (incomingWins(store.get(el.id), el)) {
      store.set(el.id, el);
      accepted.push(el);
    }
  }
  return accepted;
}

/**
 * Client-side merge with the same rule; `isLocallyEdited(id)` keeps an
 * element the user is editing right now (Excalidraw discards remote copies of
 * the text being typed / element being resized). Returns the merged list in
 * local order with new remote ids appended. The runtime uses Excalidraw's own
 * `reconcileElements` (which also fixes fractional indices); this mirror pins
 * the rule in tests.
 */
export function clientMerge<T extends VersionedElement>(local: readonly T[], remote: readonly T[], isLocallyEdited: (id: string) => boolean = () => false): T[] {
  const remoteById = new Map(remote.map((r) => [r.id, r]));
  const out: T[] = [];
  const seen = new Set<string>();
  for (const l of local) {
    const r = remoteById.get(l.id);
    seen.add(l.id);
    if (r && !isLocallyEdited(l.id) && incomingWins(l, r)) out.push(r);
    else out.push(l);
  }
  for (const r of remote) if (!seen.has(r.id)) out.push(r);
  return out;
}

export class VersionLedger {
  private known = new Map<string, VersionStamp>();

  get size(): number {
    return this.known.size;
  }

  stampOf(id: string): VersionStamp | undefined {
    return this.known.get(id);
  }

  /**
   * Elements to send: unknown to the server (and not already deleted), or
   * moved past what the server is known to hold. A local copy older than the
   * known one (kept while being edited) is not sent — the server would reject it.
   */
  changed<T extends VersionedElement>(elements: readonly T[]): T[] {
    const out: T[] = [];
    for (const e of elements) {
      const k = this.known.get(e.id);
      if (!k) {
        if (!e.isDeleted) out.push(e);
      } else if (e.version > k.version || (e.version === k.version && e.versionNonce !== k.versionNonce)) {
        out.push(e);
      }
    }
    return out;
  }

  /** Record elements as sent (only call when they actually went out on an open socket). */
  markSent(elements: readonly VersionedElement[]): void {
    for (const e of elements) this.known.set(e.id, { version: e.version, versionNonce: e.versionNonce });
  }

  /**
   * Record a remote update. `current` is the scene after reconciling: when our
   * local copy won and the server already has it from us, keep that stamp;
   * otherwise the server now holds the remote copy.
   */
  markReceived(remote: readonly VersionedElement[], current: ReadonlyMap<string, VersionStamp>): void {
    for (const r of remote) {
      const cur = current.get(r.id);
      const k = this.known.get(r.id);
      if (cur && !sameStamp(cur, r) && sameStamp(k, cur) && !incomingWins(cur, r)) continue;
      // If the server sent something older than what we already sent, it still holds ours.
      if (k && !incomingWins(k, r) && !sameStamp(k, r)) continue;
      this.known.set(r.id, { version: r.version, versionNonce: r.versionNonce });
    }
  }

  /** On (re)connect: the server's full state replaces everything we believed. */
  resetFromServer(serverElements: readonly VersionedElement[]): void {
    this.known.clear();
    for (const e of serverElements) this.known.set(e.id, { version: e.version, versionNonce: e.versionNonce });
  }
}

/** Split a list into batches so one update frame stays reasonably small. */
export function batches<T>(items: readonly T[], size = 500): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}
